import {
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonPage,
  IonTitle,
  IonToolbar,
  IonIcon
} from '@ionic/react';
import { caretForward, bookOutline } from 'ionicons/icons'
import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Group lessons into sections. Each lesson gets a "section" label.
 * Sections chosen by rough topic clusters for readability.
 */
type Lesson = { id: string; title: string };
type Section = { title: string; items: Lesson[] };

const sections: Section[] = [
  {
    title: 'Foundations & Numbers',
    items: [
      { id: 'number-systems', title: 'Number Systems' },
      { id: 'irrational-number-system', title: 'I am the Irrational Number System' },
      { id: 'whats-up-with-zero', title: 'What’s Up With Zero?' },
      { id: 'factors-multiples', title: 'Factors and multiples' },
      { id: 'prime-factorization', title: 'Prime Factorization' },
      { id: 'referent', title: 'What is a Referent?' },
    ],
  },
  {
    title: 'Integers, Fractions, Exponents & Radicals',
    items: [
      { id: 'negative-addition', title: 'Negative addition' },
      { id: 'negative-multiplication', title: 'Negative multiplication' },
      { id: 'fraction-operations', title: 'Fraction operations' },
      { id: 'clearing-fractions', title: 'Clearing Fractions' },
      { id: 'exponent-laws', title: 'Exponent laws' },
      { id: 'negative-exponents', title: 'Negative Exponents' },
      { id: 'fractional-exponents', title: 'Fractional Exponents' },
      { id: 'squares-square-roots', title: 'Squares and Square Roots' },
      { id: 'cube-roots', title: 'Cube Roots' },
      { id: 'entire-mixed-radicals', title: 'Entire and Mixed Radicals' },
    ],
  },
  {
    title: 'Algebra Essentials',
    items: [
      { id: 'basic-algebraic-solving', title: 'Basic algebraic solving techniques' },
      { id: 'isolating-variable', title: 'Isolating a Variable' },
      { id: 'subtraction-with-brackets', title: 'Making Sense Out of Subtraction With Brackets' },
      { id: 'polynomial-terminology-operations', title: 'Polynomial terminology and operations' },
      { id: 'binomial-multiplication-integers', title: 'Understanding Binomial Multiplication Using Integers' },
      { id: 'binomial-multiplication-box', title: 'Binomial Multiplication – The Box Method' },
      { id: 'multiplying-trinomials', title: 'Multiplying Trinomials' },
      { id: 'factoring-trinomials', title: 'Factoring Trinomials' },
      { id: 'factoring-trinomials-two-variables', title: 'Factoring Trinomials With Two Variables' },
      { id: 'alternative-decomposition-factoring', title: 'An Alternative To Decomposition Factoring' },
      { id: 'difference-of-squares-factoring', title: 'Difference of Squares Factoring' },
      { id: 'functions', title: 'Functions' },
      { id: 'functional-notation', title: 'Functional Notation' },
      { id: 'relation-vs-function', title: 'Relation Vs Function' },
      { id: 'domain-range', title: 'Domain and Range' },
      { id: 'interpreting-graphs', title: 'Interpreting Graphs' },
    ],
  },
  {
    title: 'Linear Relations & Systems',
    items: [
      { id: 'slope-rate-of-change', title: 'Slope and Rate of Change' },
      { id: 'equations-of-lines', title: 'Equations of lines' },
      { id: 'linear-equation-forms', title: '3 Forms of a Linear Equation' },
      { id: 'intercepts', title: 'Intercepts' },
      { id: 'parallel-lines', title: 'Equations of Parallel Lines' },
      { id: 'perpendicular-lines', title: 'Equations of Perpendicular Lines' },
      { id: 'exploring-linear-systems', title: 'Exploring Systems of Linear Equations' },
      { id: 'linear-system-graphing', title: 'Solving a Linear System By Graphing' },
      { id: 'linear-system-substitution', title: 'Solving a Linear System By Substitution' },
      { id: 'linear-system-elimination', title: 'Solving a Linear System By Elimination' },
      { id: 'linear-system-solutions', title: 'Number of Solutions of a Linear System' },
    ],
  },
  {
    title: 'Geometry & Measurement',
    items: [
      { id: 'pythagorean-theorem', title: 'The Pythagorean Theorem' },
      { id: 'trigonometry-underpinnings', title: 'Trigonometry underpinnings' },
      { id: 'trigonometry-sides-angles', title: 'Trigonometry – Finding Sides and Angles' },
      { id: 'trigonometry-applications', title: 'Trigonometry Applications – Angles of Elevation and Depression' },
      { id: 'metric-imperial-systems', title: 'Metric and Imperial Systems of Measurement' },
      { id: 'proportion-unit-conversion', title: 'Using Proportion For Unit Conversion' },
      { id: 'surface-area-prisms-pyramids', title: 'Surface Area of Prisms and Pyramids' },
      { id: 'volume-prisms-pyramids', title: 'Volume of Prisms and Pyramids' },
      { id: 'surface-area-volume-cylinders', title: 'Surface Area and Volume of Cylinders' },
      { id: 'composite-objects', title: 'Composite Objects' },
    ],
  },
];

const LessonIndexPage: React.FC = () => {
  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Lessons</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {/* Grouped lesson lists */}
        <div className="px-4 py-4 space-y-6 bg-transparent dark:bg-transparent">
          {sections.map((section) => (
            <section key={section.title}>
              {/* Section header */}
              <div className="flex items-center gap-3 mb-4 px-4 bg-gray-100 dark:bg-gray-900 py-2 rounded-md">
                
                <IonIcon icon={bookOutline} color="primary" size="large" />
                  
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{section.title}</h2>
                
              </div>

              {/* Lessons grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((lesson) => (
                  <Link
                    key={lesson.id}
                    to={{
                      pathname: `/lessons/${lesson.id}`
                    }}
                    className="block rounded-xl shadow-md transition-all p-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700"
                    aria-label={lesson.title}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-8 w-8 flex-none rounded-lg bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300 grid place-items-center font-semibold">
                        {lesson.title.split(' ').slice(0,2).map(w => w[0]?.toUpperCase()).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{lesson.title}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LessonIndexPage;
